import { rem } from 'polished'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { useInView } from 'react-intersection-observer'
import styled from 'styled-components'

import {
  tripLoginModalSignUpDismiss,
  tripLoginModalSignUpView,
} from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import Image from 'components/Common/Image'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineHeartIcon from 'LuxKit/Icons/line/LineHeartIcon'
import LineUploadIcon from 'LuxKit/Icons/line/LineUploadIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import GeoContext from 'contexts/geoContext'
import usePendingLoginHandler from 'hooks/usePendingLoginHandler'
import { copyValueToClipboard } from 'lib/web/clipboardUtils'
import CopyPublicTrip from 'tripPlanner/components/TripModal/PublicTrip/CopyPublicTrip'
import { DEFAULT_TRIP_IMAGE } from 'tripPlanner/config'
import { HeaderInViewContext } from 'tripPlanner/contexts/HeaderInViewContext'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import MenuButton from 'tripPlanner/Tripkit/Core/MenuButton/MenuButton'
import type { PublicFullTrip } from 'tripPlanner/types/publicTrip'
import { formatCasualDateRange } from 'tripPlanner/utils'
import { getClipboardUrl } from 'tripPlanner/utils/publicTrip'

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  z-index: 1;
`

const ImageWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`

const DarkOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 1) 100%
  );
`

const TripHeaderCard = styled.div`
  position: relative;
  padding: ${rem(20)};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: ${rem(180)};

  @container (min-width: 640px) {
    padding: ${rem(24)} ${rem(40)};
    height: ${rem(220)};
  }
`

interface Props {
  trip: PublicFullTrip
}

const trackTripLoginModalSignUpView = () => {
  fireInteractionEvent(tripLoginModalSignUpView('save_public_trip'))
}
const trackTripLoginModalSignUpDismiss = () => {
  fireInteractionEvent(tripLoginModalSignUpDismiss('save_public_trip'))
}

function PublicTripSummaryHeader(props: Props) {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const { trip } = props

  const saveButtonRef = useRef<HTMLButtonElement | null>(null)

  const openModal = useOpenModal()
  const headerInViewContext = useContext(HeaderInViewContext)
  const [headerCardRef, headerInView] = useInView({
    triggerOnce: false, // Load it once and keep it loaded
    rootMargin: '-100px',
  })

  // const openShareModal = useCallback(() => {
  //   openModal(PublicShareTrip, { trip })
  // }, [openModal, trip])

  const copiedToClipboardMessage = intl.formatMessage({
    id: 'HqMPIMZfKjdtw-nGAJrt',
    defaultMessage: 'Copied to clipboard',
    description: 'Snackbar message shown after copying a public trip link to clipboard',
  })

  const copyLink = useCallback(() => {
    const url = getClipboardUrl(currentRegionCode, true, '', trip.id)
    copyValueToClipboard(url)
    showSnackbar(copiedToClipboardMessage, 'success')
  }, [trip.id, currentRegionCode, copiedToClipboardMessage])

  // CopyPublicTrip
  const onSave = useCallback(
    () =>
      openModal(CopyPublicTrip, {
        publicTrip: trip,
      }),
    [openModal, trip],
  )

  const onSaveClick = usePendingLoginHandler(
    onSave,
    'tripPlannerLogin',
    trackTripLoginModalSignUpView,
    trackTripLoginModalSignUpDismiss,
  )

  useEffect(() => {
    headerInViewContext.setInView(headerInView)
  }, [headerInViewContext, headerInView])

  const tripImage = useMemo(
    (): App.Image => ({
      id: trip.imageId,
      url: trip.imageId ? undefined : DEFAULT_TRIP_IMAGE,
      title: trip.name,
    }),
    [trip],
  )

  const dateRangeText = trip.startDate ?
      formatCasualDateRange(trip.startDate, trip.endDate) :
    undefined

  const publicTripLabel = intl.formatMessage({
    id: 'JG4utJuDx-j-QPRf_Kmv',
    defaultMessage: 'Public Trip',
    description: 'Label displayed on the public trip header menu button',
  })

  const shareLabel = intl.formatMessage({
    id: 'BY4W8XYXtRVx7RbeAqmS',
    defaultMessage: 'Share',
    description: 'Menu item text to share a public trip link in the trip planner',
  })

  const somedayLabel = intl.formatMessage({
    id: 'Ga4iGj2GdOkDykqff5aI',
    defaultMessage: 'Someday',
    description: 'Fallback date text displayed when a trip has no dates set',
  })

  const saveTripLabel = intl.formatMessage({
    id: 'n-0tsBkExG-o8byLMzdx',
    defaultMessage: 'Save trip',
    description: 'Action button label to save a public trip to user account',
  })

  return (
    <TripHeaderCard ref={headerCardRef}>
      <ImageWrapper>
        <Image image={tripImage} fit="center" dpr={2} height={220} />
        <DarkOverlay />
      </ImageWrapper>
      <Container>
        <Group direction="horizontal" horizontalAlign="end">
          <MenuButton
            title={publicTripLabel}
            bodyContent={
              <ClickableListItem
                startIcon={<LineUploadIcon size="S" />}
                title={shareLabel}
                onClick={copyLink}
              />
            }
            size="large"
            variant="filled"
          />
        </Group>
        <Group
          direction="horizontal"
          horizontalAlign="space-between"
          verticalAlign="end"
        >
          <VerticalSpacer gap={8}>
            <Heading wrap="word-wrap" colour="neutral-eight" variant="heading2">
              {trip.name}
            </Heading>
            {dateRangeText && (
              <BodyTextBlock
                variant="medium"
                colour="neutral-eight"
                weight="bold"
                startIcon={<LineCalendarIcon />}
              >
                {dateRangeText}
              </BodyTextBlock>
            )}
            {!dateRangeText && (
              <BodyText
                colour="neutral-eight"
                variant="medium"
                weight="bold"
              >
                {somedayLabel}
              </BodyText>
            )}
          </VerticalSpacer>
          <CSSBreakpoint min="desktop">
            <TextButton
              onClick={onSaveClick}
              shape="round"
              startIcon={<LineHeartIcon />}
              kind="primary"
              variant="ghost"
              ref={saveButtonRef}
            >
              {saveTripLabel}
            </TextButton>
          </CSSBreakpoint>
        </Group>
      </Container>
    </TripHeaderCard>
  )
}

export default PublicTripSummaryHeader
